import React, { useState } from 'react';
import { ArrowLeft, Plus, Search, Filter, Calendar, User, DollarSign, Clock, RotateCcw, Edit, Save, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface Salario {
  id: string;
  funcionario: string;
  cargo: string;
  valorBruto: number;
  descontoINSS?: number;
  descontoIRPF?: number;
  outrosDescontos?: number;
  salarioLiquido: number;
  dataVencimento: number; // dia do mês (1-31)
  status: 'pago' | 'pendente' | 'atrasado';
  observacoes?: string;
  ativo: boolean;
  dataInicio: string;
  dataFim?: string;
  tipoFundoGarantia?: 'FGTS' | 'FGTM';
  calcularINSS?: boolean;
  calcularIRPF?: boolean;
  // Manter compatibilidade com dados antigos
  valor?: number;
}



const SalariosPage: React.FC = () => {
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Partial<Salario>>({});
  const [filtros, setFiltros] = useState({
    funcionario: '',
    cargo: '',
    status: '',
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear()
  });

  // Dados mockados
  const [salarios, setSalarios] = useState<Salario[]>([
    {
      id: '1',
      funcionario: 'João Silva',
      cargo: 'Pastor',
      valorBruto: 3500.00,
      descontoINSS: 490.00,
      descontoIRPF: 0,
      salarioLiquido: 3010.00,
      dataVencimento: 5,
      status: 'pago',
      observacoes: 'Salário pastoral',
      ativo: true,
      dataInicio: '2024-01-01',
      tipoFundoGarantia: 'FGTS',
      calcularINSS: true,
      calcularIRPF: false,
      valor: 3500.00 // compatibilidade
    },
    {
      id: '2',
      funcionario: 'Maria Santos',
      cargo: 'Secretária',
      valorBruto: 1800.00,
      descontoINSS: 162.00,
      descontoIRPF: 0,
      salarioLiquido: 1638.00,
      dataVencimento: 10,
      status: 'pendente',
      observacoes: 'Meio período',
      ativo: true,
      dataInicio: '2024-03-01',
      tipoFundoGarantia: 'FGTS',
      calcularINSS: true,
      calcularIRPF: false,
      valor: 1800.00 // compatibilidade
    },
    {
      id: '3',
      funcionario: 'Pedro Costa',
      cargo: 'Zelador',
      valorBruto: 1400.00,
      descontoINSS: 105.00,
      outrosDescontos: 50.00,
      salarioLiquido: 1245.00,
      dataVencimento: 15,
      status: 'atrasado',
      observacoes: 'Limpeza e manutenção',
      ativo: true,
      dataInicio: '2024-01-01',
      tipoFundoGarantia: 'FGTS',
      calcularINSS: true,
      calcularIRPF: false,
      valor: 1400.00 // compatibilidade
    }
  ]);

  const handleEditSalario = (salario: Salario) => {
    setEditingId(salario.id);
    setEditingData(salario);
  };

  const handleSaveEdit = () => {
    if (editingId && editingData) {
      setSalarios(prev => prev.map(s => 
        s.id === editingId ? { ...s, ...editingData } : s
      ));
      setEditingId(null);
      setEditingData({});
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleEditFieldChange = (field: keyof Salario, value: string | number | boolean) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  };

  const handleStatusClick = (id: string) => {
    if (editingId !== id) { // Só permite mudança de status se não estiver editando
      setSalarios(prev => prev.map(salario => {
        if (salario.id === id) {
          const statusOrder: ('pago' | 'pendente' | 'atrasado')[] = ['pago', 'pendente', 'atrasado'];
          const currentIndex = statusOrder.indexOf(salario.status);
          const nextIndex = (currentIndex + 1) % statusOrder.length;
          return { ...salario, status: statusOrder[nextIndex] };
        }
        return salario;
      }));
    }
  };

  const salariosAtivos = salarios.filter(s => s.ativo);
  const totalSalarios = salariosAtivos.reduce((acc, salario) => acc + (salario.valorBruto || salario.valor || 0), 0);
  const salariosVencidos = salariosAtivos.filter(s => s.status === 'atrasado').length;
  const salariosPendentes = salariosAtivos.filter(s => s.status === 'pendente').length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pago': return 'bg-green-600 dark:bg-green-500';
      case 'pendente': return 'bg-yellow-600 dark:bg-yellow-500';
      case 'atrasado': return 'bg-destructive';
      default: return 'bg-muted-foreground';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pago': return 'Pago';
      case 'pendente': return 'Pendente';
      case 'atrasado': return 'Atrasado';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/financas/administracao')}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Salários</h1>
              <p className="text-muted-foreground">Gestão de folha de pagamento e salários recorrentes</p>
            </div>
          </div>
          <Button onClick={() => navigate('/financas/salarios/novo')} className="bg-primary hover:bg-primary/90">
            <Plus className="h-4 w-4 mr-2" />
            Cadastrar Salário
          </Button>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Mensal</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 text-green-600 dark:text-green-500 mr-2" />
                <span className="text-2xl font-bold text-green-600 dark:text-green-500">
                  R$ {totalSalarios.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Funcionários Ativos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <User className="h-4 w-4 text-blue-600 dark:text-blue-500 mr-2" />
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-500">{salariosAtivos.length}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pendentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mr-2" />
                <span className="text-2xl font-bold text-yellow-600 dark:text-yellow-500">{salariosPendentes}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Atrasados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <RotateCcw className="h-4 w-4 text-destructive mr-2" />
                <span className="text-2xl font-bold text-destructive">{salariosVencidos}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros e Tabela de Salários */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtros e Registros de Salários
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Filtros */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="filtro-funcionario">Funcionário</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      id="filtro-funcionario"
                      placeholder="Buscar funcionário..."
                      value={filtros.funcionario}
                      onChange={(e) => setFiltros({...filtros, funcionario: e.target.value})}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="filtro-cargo">Cargo</Label>
                  <Input
                    id="filtro-cargo"
                    placeholder="Buscar cargo..."
                    value={filtros.cargo}
                    onChange={(e) => setFiltros({...filtros, cargo: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="filtro-status">Status</Label>
                  <Select value={filtros.status} onValueChange={(value) => setFiltros({...filtros, status: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos os status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os status</SelectItem>
                      <SelectItem value="pago">Pago</SelectItem>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="atrasado">Atrasado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filtro-mes">Mês</Label>
                  <Select value={filtros.mes.toString()} onValueChange={(value) => setFiltros({...filtros, mes: parseInt(value)})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 12}, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {new Date(2024, i).toLocaleDateString('pt-BR', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filtro-ano">Ano</Label>
                  <Select value={filtros.ano.toString()} onValueChange={(value) => setFiltros({...filtros, ano: parseInt(value)})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2024">2024</SelectItem>
                      <SelectItem value="2023">2023</SelectItem>
                      <SelectItem value="2022">2022</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tabela */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">FUNCIONÁRIO</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">CARGO</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">VALOR BRUTO</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">DESCONTO INSS</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">DESCONTO IRPF</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">OUTROS DESCONTOS</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">SALÁRIO LÍQUIDO</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">TIPO DO FUNDO</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">VENCIMENTO</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">STATUS</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">AÇÕES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salariosAtivos.map((salario) => (
                      <tr key={salario.id} className="border-b">
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <User className="h-4 w-4 text-muted-foreground mr-2" />
                            {editingId === salario.id ? (
                              <Input
                                value={editingData.funcionario || ''}
                                onChange={(e) => handleEditFieldChange('funcionario', e.target.value)}
                                className="w-full"
                              />
                            ) : (
                              <span>{salario.funcionario}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {editingId === salario.id ? (
                            <Input
                              value={editingData.cargo || ''}
                              onChange={(e) => handleEditFieldChange('cargo', e.target.value)}
                              className="w-full"
                            />
                          ) : (
                            salario.cargo
                          )}
                        </td>
                        <td className="py-3 px-4 font-medium">
                          {editingId === salario.id ? (
                            <Input
                              type="number"
                              value={editingData.valorBruto || ''}
                              onChange={(e) => handleEditFieldChange('valorBruto', parseFloat(e.target.value) || 0)}
                              className="w-full"
                            />
                          ) : (
                            `R$ ${salario.valorBruto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {editingId === salario.id ? (
                            <Input
                              type="number"
                              value={editingData.descontoINSS || ''}
                              onChange={(e) => handleEditFieldChange('descontoINSS', parseFloat(e.target.value) || 0)}
                              className="w-full"
                            />
                          ) : (
                            `R$ ${(salario.descontoINSS || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {editingId === salario.id ? (
                            <Input
                              type="number"
                              value={editingData.descontoIRPF || ''}
                              onChange={(e) => handleEditFieldChange('descontoIRPF', parseFloat(e.target.value) || 0)}
                              className="w-full"
                            />
                          ) : (
                            `R$ ${(salario.descontoIRPF || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {editingId === salario.id ? (
                            <Input
                              type="number"
                              value={editingData.outrosDescontos || ''}
                              onChange={(e) => handleEditFieldChange('outrosDescontos', parseFloat(e.target.value) || 0)}
                              className="w-full"
                            />
                          ) : (
                            `R$ ${(salario.outrosDescontos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                          )}
                        </td>
                        <td className="py-3 px-4 font-medium text-green-600 dark:text-green-500">
                          {editingId === salario.id ? (
                            <Input
                              type="number"
                              value={editingData.salarioLiquido || ''}
                              onChange={(e) => handleEditFieldChange('salarioLiquido', parseFloat(e.target.value) || 0)}
                              className="w-full"
                            />
                          ) : (
                            `R$ ${salario.salarioLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {editingId === salario.id ? (
                            <Select
                              value={editingData.tipoFundoGarantia || salario.tipoFundoGarantia || ''}
                              onValueChange={(value) => handleEditFieldChange('tipoFundoGarantia', value)}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="FGTS">FGTS</SelectItem>
                                <SelectItem value="FGTM">FGTM</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="outline">
                              {salario.tipoFundoGarantia || 'N/A'}
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-muted-foreground mr-2" />
                            {editingId === salario.id ? (
                              <Input
                                type="number"
                                min="1"
                                max="31"
                                value={editingData.dataVencimento || ''}
                                onChange={(e) => handleEditFieldChange('dataVencimento', parseInt(e.target.value) || 1)}
                                className="w-20"
                              />
                            ) : (
                              <span>Dia {salario.dataVencimento}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {editingId === salario.id ? (
                            <Select
                              value={editingData.status || salario.status}
                              onValueChange={(value) => handleEditFieldChange('status', value)}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pago">Pago</SelectItem>
                                <SelectItem value="pendente">Pendente</SelectItem>
                                <SelectItem value="atrasado">Atrasado</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge 
                              className={`cursor-pointer ${getStatusColor(salario.status)} relative group`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleStatusClick(salario.id);
                              }}
                              title="Clique para mudar status"
                            >
                              {getStatusText(salario.status)}
                              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                Clique para mudar status
                              </span>
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {editingId === salario.id ? (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleSaveEdit}
                                className="bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700"
                              >
                                <Save className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditSalario(salario)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SalariosPage;
export { SalariosPage };